import { Column, Label, Text } from '@ydistri/ds';
import { addToast } from '@ydistri/utils';
import { Checkbox, DatePicker, Input, Modal, Select } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React, { useCallback, useMemo, useState } from 'react';
import { MdWarning } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { useGetProjectStoresQuery } from '../../apis/apiLists';
import { DESCRIPTION_ITEM_MAX_LENGTH } from '../../components/global/_constants';
import {
  useDisplayStoreComparator,
  useDisplayStoreLabelFormatter,
} from '../../hooks/useDisplayStoreLabel';
import { ReduxState, useAppDispatch } from '../../store';
import {
  useCreateStoreScheduleMutation,
  useGetStoreSchedulesQuery,
  useUpdateStoreScheduleMutation,
} from '../ProjectAdministration/StoresSchedule/apiStoreSchedules';
import {
  buildPayload,
  DateRange,
  deriveFormState,
  EMPTY_FORM,
  findActiveSiblings,
  FormErrors,
  hasErrors,
  NO_SERVER_ANSWER,
  offersDifferentPeriods,
  registerDate,
  serverAnswer,
  showsTwoRanges,
  singleRangeSide,
  StoreScheduleForm,
  validate,
} from '../ProjectAdministration/StoresSchedule/storeScheduleFormLib';
import { problemsOfError } from '../Configuration/Validator/problemsLib';
import {
  describeRestrictions,
  VOCABULARY_NOTICE,
} from '../ProjectAdministration/StoresSchedule/storesScheduleLib';
import { AvailableModals, closeModal } from './modalSlice';

const { RangePicker } = DatePicker;

const modalId = AvailableModals.STORE_SCHEDULE_EDIT;

const toPickerValue = (range: DateRange): [Dayjs | null, Dayjs | null] => [
  range.from ? dayjs(range.from) : null,
  range.to ? dayjs(range.to) : null,
];

const fromPickerValue = (values: [Dayjs | null, Dayjs | null] | null): DateRange => {
  const [from, to] = values ?? [null, null];
  return {
    from: from ? from.format('YYYY-MM-DD') : null,
    to: to ? to.format('YYYY-MM-DD') : null,
  };
};

const StoreScheduleModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const editedRecord = useSelector(
    (state: ReduxState) => state.modal.modalData[modalId]?.storeSchedule,
  );
  const isCreate = editedRecord === undefined;

  const { data: stores } = useGetProjectStoresQuery();
  const { data: storeSchedules } = useGetStoreSchedulesQuery();
  const formatStoreLabel = useDisplayStoreLabelFormatter();
  const compareStores = useDisplayStoreComparator();

  const [createStoreSchedule, { isLoading: creating }] = useCreateStoreScheduleMutation();
  const [updateStoreSchedule, { isLoading: updating }] = useUpdateStoreScheduleMutation();

  const [form, setForm] = useState<StoreScheduleForm>(() =>
    editedRecord ? deriveFormState(editedRecord) : EMPTY_FORM,
  );
  // Errors are shown per field once it has been touched: the button being disabled explains an
  // untouched form on its own, and a fresh modal should not open already scolding.
  const [touched, setTouched] = useState<Partial<Record<keyof FormErrors, boolean>>>({});
  // What the server refused last time; shown under the field until the user changes something.
  const [server, setServer] = useState(NO_SERVER_ANSWER);

  const today = useMemo(
    () => registerDate(storeSchedules, dayjs().format('YYYY-MM-DD')),
    [storeSchedules],
  );

  const patch = useCallback((change: Partial<StoreScheduleForm>, touch: (keyof FormErrors)[]) => {
    setForm(current => ({ ...current, ...change }));
    setServer(NO_SERVER_ANSWER);
    setTouched(current => ({
      ...current,
      ...Object.fromEntries(touch.map(field => [field, true])),
    }));
  }, []);

  const errors = useMemo(() => validate(form, today, isCreate), [form, today, isCreate]);
  const errorFor = useCallback(
    (field: keyof FormErrors) =>
      (touched[field] ? errors[field] : undefined) ?? server.fields[field],
    [touched, errors, server],
  );
  // The whole record's period: local once either range was touched, else what the server said
  const periodError =
    (touched.send || touched.receive ? errors.period : undefined) ?? server.fields.period;

  const siblings = useMemo(
    () => findActiveSiblings(form, today, storeSchedules ?? [], editedRecord?.storeScheduleId),
    [form, today, storeSchedules, editedRecord],
  );

  // A record's store may no longer be among the visible ones; the record carries it, so the
  // read-only select still shows its name rather than a bare id.
  const storeOptions = useMemo(() => {
    const options = Array.from(stores?.array ?? []);
    if (editedRecord && !stores?.object[editedRecord.store.id]) options.push(editedRecord.store);
    return options.sort(compareStores).map(store => ({
      value: store.id,
      label: formatStoreLabel(store.name, store.customerStoreId, store.code),
    }));
  }, [stores, editedRecord, compareStores, formatStoreLabel]);

  const handleClose = useCallback(() => dispatch(closeModal()), [dispatch]);

  const handleSubmit = useCallback(() => {
    const payload = buildPayload(form);
    const request = editedRecord
      ? updateStoreSchedule({ storeScheduleId: editedRecord.storeScheduleId, payload })
      : createStoreSchedule(payload);

    request
      .unwrap()
      .then(() => {
        dispatch(addToast({ message: editedRecord ? 'Closure updated' : 'Closure created' }));
        handleClose();
      })
      .catch((error: unknown) => {
        const answer = serverAnswer(problemsOfError(error));
        setServer(answer);
        const fallback = editedRecord ? 'Closure was not updated' : 'Closure was not created';
        dispatch(
          addToast({
            message: answer.other.length > 0 ? answer.other.join(' ') : fallback,
            isError: true,
          }),
        );
      });
  }, [form, editedRecord, updateStoreSchedule, createStoreSchedule, dispatch, handleClose]);

  const singleSide = singleRangeSide(form);
  const twoRanges = showsTwoRanges(form);
  const singleError = errorFor(singleSide);

  return (
    <Modal
      title={isCreate ? 'Add closure to stores schedule' : 'Edit closure'}
      centered
      open={true}
      okText={isCreate ? 'Create' : 'Save'}
      okButtonProps={{ loading: creating || updating, disabled: hasErrors(errors) }}
      onOk={handleSubmit}
      onCancel={handleClose}
    >
      <Column $gap="1rem">
        <Column $gap="0.25rem">
          <Label>Store</Label>
          <Select
            showSearch
            optionFilterProp="label"
            placeholder="Pick a store"
            value={form.storeId}
            options={storeOptions}
            onChange={storeId => patch({ storeId }, ['store'])}
            disabled={!isCreate}
            data-testid="store-schedule-store"
          />
          {errorFor('store') && <Text $type="danger">{errorFor('store')}</Text>}
        </Column>

        <Column $gap="0.25rem">
          <Label>What is restricted</Label>
          <Checkbox
            checked={form.cannotReceive}
            onChange={e => patch({ cannotReceive: e.target.checked }, ['restrictions', 'receive'])}
          >
            Cannot receive
          </Checkbox>
          <Checkbox
            checked={form.cannotSend}
            onChange={e => patch({ cannotSend: e.target.checked }, ['restrictions', 'send'])}
          >
            Cannot send
          </Checkbox>
          {errorFor('restrictions') && <Text $type="danger">{errorFor('restrictions')}</Text>}
        </Column>

        <Text $type="light">{VOCABULARY_NOTICE}</Text>

        <Column $gap="0.25rem" $alignItems="flex-start">
          <Label>When</Label>
          {offersDifferentPeriods(form) && (
            <Checkbox
              checked={form.differentPeriods}
              onChange={e => patch({ differentPeriods: e.target.checked }, [])}
            >
              Different periods
            </Checkbox>
          )}
          {twoRanges ? (
            <>
              <Label>Cannot receive from – to</Label>
              <RangePicker
                value={toPickerValue(form.receive)}
                onChange={values => patch({ receive: fromPickerValue(values) }, ['receive'])}
              />
              {errorFor('receive') && <Text $type="danger">{errorFor('receive')}</Text>}
              <Label>Cannot send from – to</Label>
              <RangePicker
                value={toPickerValue(form.send)}
                onChange={values => patch({ send: fromPickerValue(values) }, ['send'])}
              />
              {errorFor('send') && <Text $type="danger">{errorFor('send')}</Text>}
            </>
          ) : (
            <>
              <RangePicker
                value={toPickerValue(form[singleSide])}
                onChange={values => patch({ [singleSide]: fromPickerValue(values) }, [singleSide])}
              />
              {singleError && <Text $type="danger">{singleError}</Text>}
            </>
          )}
          {periodError && <Text $type="danger">{periodError}</Text>}
          {siblings.length > 0 && (
            <Text $type="warning">
              <MdWarning size={16} /> This store already has another active closure:{' '}
              {siblings.map(describeRestrictions).join('; ')}
            </Text>
          )}
        </Column>

        <Column $gap="0.25rem">
          <Label>Note</Label>
          <Input.TextArea
            rows={2}
            maxLength={DESCRIPTION_ITEM_MAX_LENGTH}
            value={form.note}
            onChange={e => patch({ note: e.target.value }, [])}
          />
        </Column>
      </Column>
    </Modal>
  );
};

export default StoreScheduleModal;
